import React, { useState } from 'react';
import { 
  ClipboardDocumentListIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  FunnelIcon,
  EyeIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  MapPinIcon,
  DocumentArrowDownIcon,
  ChevronDownIcon,
  ArrowsRightLeftIcon,
  ShareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useWorksheets } from '../../hooks/useWorksheets';
import { useCustomers } from '../../hooks/useCustomers';
import { useLanguage } from '../../contexts/LanguageContext';
import { Worksheet, CreateWorksheetData, WorksheetStatus, WORKSHEET_STATUSES } from '../../types/worksheet';
import LoadingSpinner from '../common/LoadingSpinner';
import { Tooltip } from '../common/Tooltip';
import WorksheetForm from './WorksheetForm';
import WorksheetView from './WorksheetView';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import toast from 'react-hot-toast';
import Pagination from '../common/Pagination';
import { generateWorksheetPdf } from '../../lib/worksheetPdfGenerator';

interface WorksheetListProps {
  triggerNewForm?: boolean;
  onFormOpened?: () => void;
  onDeleteClick?: (worksheet: Worksheet) => void;
  searchTerm?: string;
}

const WorksheetList: React.FC<WorksheetListProps> = ({
  triggerNewForm = false,
  onFormOpened,
  onDeleteClick,
  searchTerm = ''
}) => {
  const { worksheets: hookWorksheets, loading, deleteWorksheet, addWorksheet, updateWorksheet, bulkChangeStatus, bulkDelete, exportToCsv, generatePublicLink, convertToQuotation, convertFromSurvey, createDeployment, getDeployments, deductStockForWorksheet, customFields, customStatuses, loadCustomFields, loadCustomStatuses } = useWorksheets();
  const worksheets = hookWorksheets; // Mindig a hook adatait használjuk
  const { customers } = useCustomers();
  const { t } = useLanguage();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('completed');
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [deploymentParent, setDeploymentParent] = useState<Worksheet | null>(null);
  const [deploymentTitle, setDeploymentTitle] = useState('');
  const [deploymentDate, setDeploymentDate] = useState('');
  const [deploymentStart, setDeploymentStart] = useState('');
  const [deploymentEnd, setDeploymentEnd] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Helper function to safely format dates
  const formatDate = (date: any) => {
    if (!date) return '';
    
    try {
      // Handle Firestore Timestamp objects
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd.', { locale: hu });
      }
      
      // Handle Date objects
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd.', { locale: hu });
      }
      
      // Handle date strings
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd.', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };

  // Helper function to safely format dates for desktop view
  const formatDateShort = (date: any) => {
    if (!date) return '';
    
    try {
      // Handle Firestore Timestamp objects
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'MM/dd', { locale: hu });
      }
      
      // Handle Date objects
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'MM/dd', { locale: hu });
      }
      
      // Handle date strings
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'MM/dd', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };

  // Handle triggerNewForm prop
  React.useEffect(() => {
    if (triggerNewForm) {
      setSelectedWorksheet(null);
      setIsFormOpen(true);
      onFormOpened?.();
    }
  }, [triggerNewForm, onFormOpened]);

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return t('worksheets.fields.unknownCustomer');
    
    return customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
  };

  const handleDownloadPdf = (worksheet: Worksheet) => {
    try {
      generateWorksheetPdf({
        worksheet,
        timeEntries: [],
        customerName: getCustomerName(worksheet.customerId),
        t,
      });
      toast.success(t('worksheets.messages.pdfSuccess'));
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(t('worksheets.messages.pdfError'));
    }
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500',
      assigned: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700',
      in_progress: 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700',
      review_pending: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700',
      completed: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700',
      sent: 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700',
      partially_invoiced: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700',
      invoiced: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
      cancelled: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700',
      hibernated: 'bg-slate-100 dark:bg-slate-900/20 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    };
    return map[status] || 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
  };

  const getStatusIcon = (status: string) => {
    const map: Record<string, React.ReactNode> = {
      draft: <ClipboardDocumentListIcon className="h-4 w-4" />,
      assigned: <UserIcon className="h-4 w-4" />,
      in_progress: <ClockIcon className="h-4 w-4" />,
      review_pending: <MagnifyingGlassIcon className="h-4 w-4" />,
      completed: <CheckCircleIcon className="h-4 w-4" />,
      sent: <DocumentTextIcon className="h-4 w-4" />,
      partially_invoiced: <ExclamationCircleIcon className="h-4 w-4" />,
      invoiced: <CheckCircleIcon className="h-4 w-4" />,
      cancelled: <XCircleIcon className="h-4 w-4" />,
      hibernated: <ShieldExclamationIcon className="h-4 w-4" />,
    };
    return map[status] || <ClipboardDocumentListIcon className="h-4 w-4" />;
  };

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'maintenance': return <CogIcon className="h-4 w-4" />;
      case 'repair': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'inspection': return <MagnifyingGlassIcon className="h-4 w-4" />;
      case 'other': return <ClipboardDocumentListIcon className="h-4 w-4" />;
      default: return <ClipboardDocumentListIcon className="h-4 w-4" />;
    }
  };

  const getWorkTypeLabel = (type: string) => {
    return t(`worksheets.workTypes.${type}`);
  };

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'installation': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'maintenance': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'repair': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'inspection': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      case 'other': return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getStatusColorHex = (status: string) => {
    const map: Record<string, string> = {
      draft: '#6b7280',
      assigned: '#2563eb',
      in_progress: '#d97706',
      review_pending: '#7c3aed',
      completed: '#059669',
      sent: '#0891b2',
      partially_invoiced: '#ea580c',
      invoiced: '#059669',
      cancelled: '#dc2626',
      hibernated: '#475569',
    };
    return map[status] || '#6b7280';
  };

  const getPriorityColorHex = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  };

  const getWorkTypeColorHex = (type: string) => {
    switch (type) {
      case 'installation': return '#2563eb';
      case 'maintenance': return '#059669';
      case 'repair': return '#dc2626';
      case 'inspection': return '#7c3aed';
      case 'other': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const filteredWorksheets = worksheets.filter(worksheet => {
    const statusMatch = filterStatus === 'all' || worksheet.status === filterStatus;
    const typeMatch = filterType === 'all' || worksheet.workType === filterType;
    const priorityMatch = filterPriority === 'all' || worksheet.priority === filterPriority;
    
    // Search functionality
    let searchMatch = true;
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      const customerName = getCustomerName(worksheet.customerId).toLowerCase();
      const workTypeLabel = (getWorkTypeLabel(worksheet.workType) || '').toLowerCase();
      
      searchMatch = 
        worksheet.title.toLowerCase().includes(query) ||
        worksheet.worksheetNumber.toLowerCase().includes(query) ||
        customerName.includes(query) ||
        workTypeLabel.includes(query) ||
        (worksheet.description?.toLowerCase().includes(query) || false) ||
        (worksheet.location?.toLowerCase().includes(query) || false);
    }
    
    return statusMatch && typeMatch && priorityMatch && searchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredWorksheets.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentWorksheets = filteredWorksheets.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleEdit = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setIsFormOpen(true);
  };

  const handleView = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setIsViewOpen(true);
  };

  const handleDelete = async (worksheet: Worksheet) => {
    try {
      await deleteWorksheet(worksheet.id);
      toast.success(t('worksheets.messages.deleteSuccess'));
    } catch (error) {
      toast.error(t('worksheets.messages.deleteError'));
    }
  };

  const handleFormSubmit = async (data: CreateWorksheetData) => {
    try {
      if (selectedWorksheet) {
        await updateWorksheet(selectedWorksheet.id, data);
        toast.success(t('worksheets.messages.updateSuccess'));
      } else {
        await addWorksheet(data);
        toast.success(t('worksheets.messages.createSuccess'));
      }
      setIsFormOpen(false);
      setSelectedWorksheet(null);
    } catch (error) {
      toast.error(
        selectedWorksheet 
          ? t('worksheets.messages.updateError')
          : t('worksheets.messages.createError')
      );
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setSelectedWorksheet(null);
  };

  const handleStatusChange = async (worksheet: Worksheet, newStatus: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        status: newStatus as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet status:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  const handlePriorityChange = async (worksheet: Worksheet, newPriority: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        priority: newPriority as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet priority:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  const handleWorkTypeChange = async (worksheet: Worksheet, newWorkType: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        workType: newWorkType as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet work type:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === currentWorksheets.length && currentWorksheets.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentWorksheets.map(w => w.id)));
    }
  };

  const handleBulkStatusChange = async () => {
    try {
      await bulkChangeStatus(Array.from(selectedIds), bulkStatusValue as WorksheetStatus);
      setSelectedIds(new Set());
      setShowBulkStatusModal(false);
    } catch (error) {
      console.error('Error bulk changing status:', error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  const handleExportCsv = () => {
    const toExport = selectedIds.size > 0
      ? filteredWorksheets.filter(w => selectedIds.has(w.id))
      : filteredWorksheets;
    exportToCsv(toExport, getCustomerName);
  };

  const handleShareLink = async (worksheet: Worksheet) => {
    try {
      const link = await generatePublicLink(worksheet.id);
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success('Publikus link vágólapra másolva');
      }
    } catch (error) {
      console.error('Error sharing link:', error);
    }
  };

  const handleConvertToQuotation = async (worksheet: Worksheet) => {
    const quotationId = await convertToQuotation(worksheet.id);
    if (quotationId) {
      window.location.href = `/quotations`;
    }
  };

  const handleConvertFromSurvey = async (worksheet: Worksheet) => {
    const newId = await convertFromSurvey(worksheet.id);
    if (newId) {
      toast.success('Munkalap létrehozva felmérésből');
    }
  };

  const handleStartDeployment = (worksheet: Worksheet) => {
    setDeploymentParent(worksheet);
    setDeploymentTitle('');
    setDeploymentDate('');
    setDeploymentStart('');
    setDeploymentEnd('');
    setShowDeploymentModal(true);
  };

  const handleCreateDeployment = async () => {
    if (!deploymentParent || !deploymentTitle.trim()) return;
    try {
      await createDeployment(deploymentParent.id, {
        title: deploymentTitle.trim(),
        worksheetDate: deploymentDate ? new Date(deploymentDate) : undefined,
        startTime: deploymentStart || undefined,
        endTime: deploymentEnd || undefined,
      });
      setShowDeploymentModal(false);
      setDeploymentParent(null);
    } catch (error) {
      console.error('Error creating deployment:', error);
    }
  };

  const handleStockDeduction = async (worksheet: Worksheet) => {
    await deductStockForWorksheet(worksheet.id);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="lg:hidden w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          <span className="inline-flex items-center gap-2"><FunnelIcon className="h-4 w-4 text-gray-400" />{t('common.filters')}{(filterStatus !== 'all' || filterType !== 'all' || filterPriority !== 'all') && <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />}</span>
          <ChevronDownIcon className={`h-5 w-5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
        </button>
        <div className={`p-4 ${filtersOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Status filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.status')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterStatus === 'all'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.all')}
              </button>
              {WORKSHEET_STATUSES.map(s => (
                <button
                  key={s.value}
                  onClick={() => setFilterStatus(s.value)}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                    filterStatus === s.value
                      ? 'text-white'
                      : 'hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: filterStatus === s.value ? getStatusColorHex(s.value) : getStatusColorHex(s.value) + '30',
                    color: filterStatus === s.value ? '#ffffff' : getStatusColorHex(s.value)
                  }}
                >
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Type filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.workType')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterType('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterType === 'all'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.allTypes')}
              </button>
              <button
                onClick={() => setFilterType('installation')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'installation'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'installation' ? getWorkTypeColorHex('installation') : getWorkTypeColorHex('installation') + '30',
                  color: filterType === 'installation' ? '#ffffff' : getWorkTypeColorHex('installation')
                }}
              >
                {t('worksheets.workTypes.installation')}
              </button>
              <button
                onClick={() => setFilterType('maintenance')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'maintenance'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'maintenance' ? getWorkTypeColorHex('maintenance') : getWorkTypeColorHex('maintenance') + '30',
                  color: filterType === 'maintenance' ? '#ffffff' : getWorkTypeColorHex('maintenance')
                }}
              >
                {t('worksheets.workTypes.maintenance')}
              </button>
              <button
                onClick={() => setFilterType('repair')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'repair'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'repair' ? getWorkTypeColorHex('repair') : getWorkTypeColorHex('repair') + '30',
                  color: filterType === 'repair' ? '#ffffff' : getWorkTypeColorHex('repair')
                }}
              >
                {t('worksheets.workTypes.repair')}
              </button>
              <button
                onClick={() => setFilterType('inspection')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'inspection'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'inspection' ? getWorkTypeColorHex('inspection') : getWorkTypeColorHex('inspection') + '30',
                  color: filterType === 'inspection' ? '#ffffff' : getWorkTypeColorHex('inspection')
                }}
              >
                {t('worksheets.workTypes.inspection')}
              </button>
            </div>
          </div>

          {/* Priority filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.priority')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterPriority('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterPriority === 'all'
                    ? 'bg-slate-600 text-white border-slate-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.all')}
              </button>
              <button
                onClick={() => setFilterPriority('high')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'high'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'high' ? getPriorityColorHex('high') : getPriorityColorHex('high') + '30',
                  color: filterPriority === 'high' ? '#ffffff' : getPriorityColorHex('high')
                }}
              >
                {t('worksheets.priority.high')}
              </button>
              <button
                onClick={() => setFilterPriority('medium')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'medium'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'medium' ? getPriorityColorHex('medium') : getPriorityColorHex('medium') + '30',
                  color: filterPriority === 'medium' ? '#ffffff' : getPriorityColorHex('medium')
                }}
              >
                {t('worksheets.priority.medium')}
              </button>
              <button
                onClick={() => setFilterPriority('low')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'low'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'low' ? getPriorityColorHex('low') : getPriorityColorHex('low') + '30',
                  color: filterPriority === 'low' ? '#ffffff' : getPriorityColorHex('low')
                }}
              >
                {t('worksheets.priority.low')}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 flex flex-wrap items-center gap-2">
        <button
          onClick={handleSelectAll}
          className="text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          {selectedIds.size === currentWorksheets.length && currentWorksheets.length > 0
            ? 'Kijelölés megszüntetése'
            : 'Összes kijelölése'}
        </button>
        {selectedIds.size > 0 && (
          <>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedIds.size} kijelölve
            </span>
            <button
              onClick={() => setShowBulkStatusModal(true)}
              className="text-sm px-3 py-1.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              Státusz váltás
            </button>
            <button
              onClick={handleBulkDelete}
              className="text-sm px-3 py-1.5 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              Tömeges törlés
            </button>
          </>
        )}
        <button
          onClick={handleExportCsv}
          className="text-sm px-3 py-1.5 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors ml-auto"
        >
          <DocumentArrowDownIcon className="h-4 w-4 inline mr-1" />
          CSV export {selectedIds.size > 0 ? `(${selectedIds.size})` : '(mind)'}
        </button>
      </div>

      {/* Desktop table view */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === currentWorksheets.length && currentWorksheets.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.title')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.customer')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.workType')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.status')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.priority')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.dueDate')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.deploymentAndTravel')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-32">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {currentWorksheets.map(worksheet => (
                <tr key={worksheet.id} onClick={() => handleView(worksheet)} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedIds.has(worksheet.id) ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(worksheet.id)}
                      onChange={() => handleToggleSelect(worksheet.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2">
                        <ClipboardDocumentListIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {worksheet.title}
                          {worksheet.isParent && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">Keret</span>
                          )}
                          {worksheet.parentWorksheetId && (
                            <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Kiszállás</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {worksheet.worksheetNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {getCustomerName(worksheet.customerId)}
                      </div>
                      {worksheet.location && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {worksheet.location}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.workType}
                        onChange={(e) => handleWorkTypeChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getWorkTypeColorHex(worksheet.workType) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getWorkTypeColorHex(worksheet.workType)
                        }}
                      >
                        <option value="installation">{t('worksheets.workTypes.installation')}</option>
                        <option value="maintenance">{t('worksheets.workTypes.maintenance')}</option>
                        <option value="repair">{t('worksheets.workTypes.repair')}</option>
                        <option value="inspection">{t('worksheets.workTypes.inspection')}</option>
                        <option value="other">{t('worksheets.workTypes.other')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.status}
                        onChange={(e) => handleStatusChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getStatusColorHex(worksheet.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getStatusColorHex(worksheet.status)
                        }}
                      >
                        <option value="draft">{t('worksheets.status.draft')}</option>
                        <option value="assigned">{t('worksheets.status.assigned')}</option>
                        <option value="in_progress">{t('worksheets.status.in_progress')}</option>
                        <option value="review_pending">{t('worksheets.status.review_pending')}</option>
                        <option value="completed">{t('worksheets.status.completed')}</option>
                        <option value="sent">{t('worksheets.status.sent')}</option>
                        <option value="partially_invoiced">{t('worksheets.status.partially_invoiced')}</option>
                        <option value="invoiced">{t('worksheets.status.invoiced')}</option>
                        <option value="cancelled">{t('worksheets.status.cancelled')}</option>
                        <option value="hibernated">{t('worksheets.status.hibernated')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.priority}
                        onChange={(e) => handlePriorityChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getPriorityColorHex(worksheet.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getPriorityColorHex(worksheet.priority)
                        }}
                      >
                        <option value="low">{t('worksheets.priority.low')}</option>
                        <option value="medium">{t('worksheets.priority.medium')}</option>
                        <option value="high">{t('worksheets.priority.high')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      {worksheet.dueDate && (
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {formatDateShort(worksheet.dueDate)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                        {worksheet.deploymentOccurred && (
                          <div className="flex items-center">
                            <TruckIcon className="h-3 w-3 text-green-600 dark:text-green-400 mr-1" />
                            <span>{t('worksheets.fields.deployment')}</span>
                          </div>
                        )}
                        {worksheet.travelKmTotal && (
                          <div className="flex items-center">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">{worksheet.travelKmTotal} km</span>
                          </div>
                        )}
                        
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end space-x-1">
                      <Tooltip content={t('worksheets.actions.view')}>
                        <button
                          onClick={() => handleView(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('worksheets.actions.downloadPdf')}>
                        <button
                          onClick={() => handleDownloadPdf(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('worksheets.actions.edit')}>
                        <button
                          onClick={() => handleEdit(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Publikus link">
                        <button
                          onClick={() => handleShareLink(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400"
                        >
                          <ShareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Árajánlattá alakítás">
                        <button
                          onClick={() => handleConvertToQuotation(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
                        >
                          <ArrowsRightLeftIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      {worksheet.workType === 'inspection' && (
                        <Tooltip content="Munkalappá alakítás (felmérés)">
                          <button
                            onClick={() => handleConvertFromSurvey(worksheet)}
                            className="text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400"
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip content="Kiszállás hozzáadása">
                        <button
                          onClick={() => handleStartDeployment(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Készletlevonás">
                        <button
                          onClick={() => handleStockDeduction(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400"
                        >
                          <WrenchScrewdriverIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('worksheets.actions.delete')}>
                        <button
                          onClick={() => onDeleteClick?.(worksheet)}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile list view */}
      <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden">
        {currentWorksheets.map(worksheet => {
          const dark = document.documentElement.classList.contains('dark');
          const pillStyle = (hex: string) => ({
            backgroundColor: hex + (dark ? '33' : '22'),
            color: hex,
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 20 20' fill='${encodeURIComponent(hex)}'><path d='M5.5 7.5l4.5 4.5 4.5-4.5'/></svg>")`
          });
          return (
          <div
            key={worksheet.id}
            onClick={() => handleView(worksheet)}
            className="crm-card p-3 w-full overflow-hidden cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/60"
          >
            {/* Fejléc: cím + szám, jobbra ikonok */}
            <div className="flex items-start gap-2">
              <div className="p-1.5 rounded-md bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 flex-shrink-0">
                <ClipboardDocumentListIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{worksheet.title}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {worksheet.worksheetNumber}
                  {worksheet.customerId && <span> · {getCustomerName(worksheet.customerId)}</span>}
                </div>
              </div>
              <div className="flex items-center flex-shrink-0 -mr-1" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => handleEdit(worksheet)} className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20" aria-label={t('worksheets.actions.edit')}>
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button onClick={() => onDeleteClick?.(worksheet)} className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" aria-label={t('worksheets.actions.delete')}>
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Állapot / prioritás / típus – kompakt pill-választók */}
            <div className="mt-2.5 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
              <select
                value={worksheet.status}
                onChange={(e) => handleStatusChange(worksheet, e.target.value)}
                className="crm-input-raw appearance-none h-7 text-[11px] font-medium rounded-full pl-2.5 pr-6 py-0 border-0 bg-no-repeat bg-[right_6px_center] focus:outline-none focus:ring-1 focus:ring-primary-500"
                style={pillStyle(getStatusColorHex(worksheet.status))}
              >
                <option value="draft">{t('worksheets.status.draft')}</option>
                <option value="assigned">{t('worksheets.status.assigned')}</option>
                <option value="in_progress">{t('worksheets.status.in_progress')}</option>
                <option value="review_pending">{t('worksheets.status.review_pending')}</option>
                <option value="completed">{t('worksheets.status.completed')}</option>
                <option value="sent">{t('worksheets.status.sent')}</option>
                <option value="partially_invoiced">{t('worksheets.status.partially_invoiced')}</option>
                <option value="invoiced">{t('worksheets.status.invoiced')}</option>
                <option value="cancelled">{t('worksheets.status.cancelled')}</option>
                <option value="hibernated">{t('worksheets.status.hibernated')}</option>
              </select>
              <select
                value={worksheet.priority}
                onChange={(e) => handlePriorityChange(worksheet, e.target.value)}
                className="crm-input-raw appearance-none h-7 text-[11px] font-medium rounded-full pl-2.5 pr-6 py-0 border-0 bg-no-repeat bg-[right_6px_center] focus:outline-none focus:ring-1 focus:ring-primary-500"
                style={pillStyle(getPriorityColorHex(worksheet.priority))}
              >
                <option value="low">{t('worksheets.priority.low')}</option>
                <option value="medium">{t('worksheets.priority.medium')}</option>
                <option value="high">{t('worksheets.priority.high')}</option>
              </select>
              <select
                value={worksheet.workType}
                onChange={(e) => handleWorkTypeChange(worksheet, e.target.value)}
                className="crm-input-raw appearance-none h-7 text-[11px] font-medium rounded-full pl-2.5 pr-6 py-0 border-0 bg-no-repeat bg-[right_6px_center] focus:outline-none focus:ring-1 focus:ring-primary-500"
                style={pillStyle(getWorkTypeColorHex(worksheet.workType))}
              >
                <option value="installation">{t('worksheets.workTypes.installation')}</option>
                <option value="maintenance">{t('worksheets.workTypes.maintenance')}</option>
                <option value="repair">{t('worksheets.workTypes.repair')}</option>
                <option value="inspection">{t('worksheets.workTypes.inspection')}</option>
                <option value="other">{t('worksheets.workTypes.other')}</option>
              </select>
            </div>

            {/* Meta sor */}
            {(worksheet.worksheetDate || (worksheet.startTime && worksheet.endTime) || worksheet.deploymentOccurred || (worksheet.travelKmTotal || 0) > 0 || worksheet.dueDate) && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
                {worksheet.worksheetDate && (
                  <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5 text-gray-400" />{formatDate(worksheet.worksheetDate)}</span>
                )}
                {worksheet.startTime && worksheet.endTime && (
                  <span className="inline-flex items-center gap-1"><ClockIcon className="h-3.5 w-3.5 text-gray-400" />{worksheet.startTime}–{worksheet.endTime}</span>
                )}
                {worksheet.deploymentOccurred && (
                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400"><TruckIcon className="h-3.5 w-3.5" />{t('worksheets.fields.deployment')}{(worksheet.travelKmTotal || 0) > 0 ? ` · ${worksheet.travelKmTotal} km` : ''}</span>
                )}
              </div>
            )}

            {worksheet.description && (
              <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-300 line-clamp-2">{worksheet.description}</div>
            )}

            <div className="mt-2.5 pt-2 border-t border-[var(--crm-border)] flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => handleView(worksheet)}
                className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium rounded-md text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30"
              >
                <EyeIcon className="h-3.5 w-3.5" />{t('worksheets.actions.view')}
              </button>
              <button
                onClick={() => handleDownloadPdf(worksheet)}
                className="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <DocumentArrowDownIcon className="h-3.5 w-3.5" />PDF
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredWorksheets.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      {/* Empty state */}
      {filteredWorksheets.length === 0 && (
        <div className="text-center py-12">
          <ClipboardDocumentListIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('worksheets.messages.noWorksheets')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('worksheets.messages.noWorksheetsDescription')}
          </p>
        </div>
      )}

      {/* Worksheet Form */}
      <WorksheetForm
        isOpen={isFormOpen}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        initialData={selectedWorksheet}
      />

      {/* Worksheet View */}
      {isViewOpen && selectedWorksheet && (
        <WorksheetView
          worksheet={selectedWorksheet}
          isOpen={isViewOpen}
          onClose={() => {
            setIsViewOpen(false);
            setSelectedWorksheet(null);
          }}
          onEdit={handleEdit}
        />
      )}

      {/* Bulk Status Change Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Státusz váltás ({selectedIds.size} munkalap)
            </h3>
            <select
              value={bulkStatusValue}
              onChange={(e) => setBulkStatusValue(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
            >
              {WORKSHEET_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowBulkStatusModal(false)}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Mégse
              </button>
              <button
                onClick={handleBulkStatusChange}
                className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
              >
                Mentés
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deployment Modal */}
      {showDeploymentModal && deploymentParent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
              Új kiszállás
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {deploymentParent.title} ({deploymentParent.worksheetNumber})
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Megnevezés</label>
                <input
                  type="text"
                  value={deploymentTitle}
                  onChange={(e) => setDeploymentTitle(e.target.value)}
                  placeholder="pl. Első kiszállás"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dátum</label>
                <input
                  type="date"
                  value={deploymentDate}
                  onChange={(e) => setDeploymentDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kezdés</label>
                  <input
                    type="time"
                    value={deploymentStart}
                    onChange={(e) => setDeploymentStart(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Befejezés</label>
                  <input
                    type="time"
                    value={deploymentEnd}
                    onChange={(e) => setDeploymentEnd(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => setShowDeploymentModal(false)}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Mégse
              </button>
              <button
                onClick={handleCreateDeployment}
                disabled={!deploymentTitle.trim()}
                className="px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Létrehozás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetList; 